'use client';

import React from 'react';
import { runWhenSentryReady } from '@/shared/libs/sentry-ready-queue';

type SectionErrorBoundaryProps = {
  children: React.ReactNode;
  name: string;
  fallback?: React.ReactNode;
  resetKeys?: unknown[];
  extra?: Record<string, unknown>;
};

type SectionErrorBoundaryState = {
  error: Error | null;
};

const haveResetKeysChanged = (
  prevResetKeys: unknown[] = [],
  nextResetKeys: unknown[] = []
) => {
  if (prevResetKeys.length !== nextResetKeys.length) return true;

  return prevResetKeys.some(
    (value, index) => !Object.is(value, nextResetKeys[index])
  );
};

class SectionErrorBoundary extends React.Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  state: SectionErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const { name, extra } = this.props;

    console.error(`[SectionErrorBoundary:${name}]`, error, {
      componentStack: info.componentStack,
      ...extra,
    });

    runWhenSentryReady(({ captureException }) => {
      captureException(error, {
        tags: {
          boundary: 'SectionErrorBoundary',
          section: name,
        },
        extra: {
          componentStack: info.componentStack,
          ...extra,
        },
      });
    });
  }

  componentDidUpdate(prevProps: SectionErrorBoundaryProps) {
    if (
      this.state.error &&
      haveResetKeysChanged(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({ error: null });
    }
  }

  render() {
    const { children, fallback = null } = this.props;

    if (this.state.error) {
      return fallback;
    }

    return children;
  }
}

export { SectionErrorBoundary };
